import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { integrationService, IntegrationType } from '@/services/integrationService';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { videoService } from '@/services/videoService';
import { useCompanies } from '@/hooks/api/integrations/useCompanies';

interface IntegrationFormData {
  endpoint_url: string;
  api_key?: string;
  id_plan?: string;
  event_source?: string;
  machine_code?: string;
  email_sequence_code?: string;
  sequence_level_code?: string;
  id_video: string;
}

/**
 * Hook para gerenciar formulário de integração
 * @param companyNameId - NameId da empresa (ex: 'voxuy', 'activecampaign')
 * @param companyName - Nome da empresa para mensagens
 * @param type - Tipo de integração (leads ou pixel)
 */
export const useIntegrationForm = (
  companyNameId: string,
  companyName: string,
  type: IntegrationType = 'leads'
) => {
  useLanguage();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: companies } = useCompanies();

  const [selectedVideo, setSelectedVideo] = useState<{ id: string; name: string } | null>(null);
  const [videos, setVideos] = useState<Array<{ id: string; name: string }>>([]);
  const [isLoadingVideos, setIsLoadingVideos] = useState(true);

  // Buscar vídeos do usuário
  useEffect(() => {
    const fetchVideos = async () => {
      if (!user?.id) {
        return;
      }

      try {
        setIsLoadingVideos(true);
        const userVideos = await videoService.getDashboardVideos(user.id);

        const mappedVideos = userVideos.map((video: any) => ({
          id: video.id,
          name: video.title || 'Vídeo sem nome',
        }));

        setVideos(mappedVideos);

        // Selecionar o primeiro vídeo automaticamente
        if (mappedVideos.length > 0 && !selectedVideo) {
          setSelectedVideo(mappedVideos[0]);
        }
      } catch (error) {
        console.error('useIntegrationForm: Erro ao buscar vídeos:', error);
        toast({
          title: translateInterfaceText("Erro ao buscar vídeos"),
          description: translateInterfaceText("Não foi possível carregar seus vídeos."),
          variant: 'destructive',
        });
      } finally {
        setIsLoadingVideos(false);
      }
    };

    fetchVideos();
  }, [user?.id]);

  // Mutation para criar integração
  const createMutation = useMutation({
    mutationFn: integrationService.create,
    onSuccess: () => {
      toast({
        title: translateInterfaceText("Integração criada"),
        description: translateInterfaceText("Integração com {value0} configurada com sucesso.", { value0: (companyName) }),
      });
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
    onError: (error: any) => {
      console.error('Erro ao criar integração:', error);
      toast({
        title: translateInterfaceText("Erro ao criar integração"),
        description: error?.message || translateInterfaceText("Ocorreu um erro ao configurar a integração."),
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = useCallback(
    async (formData: IntegrationFormData) => {
      if (!user?.id) {
        toast({
          title: translateInterfaceText("Erro"),
          description: translateInterfaceText("Você precisa estar autenticado."),
          variant: 'destructive',
        });
        return;
      }

      if (!selectedVideo) {
        toast({
          title: translateInterfaceText("Erro"),
          description: translateInterfaceText("Selecione um vídeo."),
          variant: 'destructive',
        });
        return;
      }

      // Buscar o ID real da empresa pelo nameId
      const company = companies?.find((c) =>
        c.name.toLowerCase() === companyName.toLowerCase()
      );

      if (!company) {
        toast({
          title: translateInterfaceText("Erro"),
          description: translateInterfaceText("Empresa {value0} não encontrada no sistema.", { value0: (companyName) }),
          variant: 'destructive',
        });
        console.error('Empresa não encontrada:', companyName, 'Companies disponíveis:', companies);
        return;
      }

      // Limpar event_source se não for tipo pixel
      const cleanedFormData = { ...formData };
      if (type !== 'pixel') {
        delete cleanedFormData.event_source;
      }

      const payload = {
        ...cleanedFormData,
        id_company: company.id,
        id_user: user.id,
        id_video: selectedVideo.id,
        type,
        status: 'active' as const,
      };

      await createMutation.mutateAsync(payload);
    },
    [user?.id, selectedVideo, companyName, companies, type, createMutation, toast]
  );

  return {
    selectedVideo,
    setSelectedVideo,
    videos,
    isLoadingVideos,
    handleSubmit,
    isSubmitting: createMutation.isPending,
  };
};
